import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Order } from '@lib/entities';
import { loggedOut } from '@lib/redux/actions/lifecycle';
import { type DeferredAction, sagaRequest, withDeferred } from '@lib/redux/deferred';

export interface OrderState {
    previousOrders: Order[];
    isLoading: boolean;
    hasMore: boolean;
    currentPage: number;
    order?: Order;
    isReorder: boolean;
}

/**
 * Request payloads. The three fetches carry an optional deferred in `meta` (lib/redux/deferred.ts)
 * so `useOrder.redux` resolves `Order[]` / `Order | undefined` like lib/state/Order.ts:25-28;
 * dispatchers that pass only the payload are unchanged.
 */
export interface OrdersPagePayload {
    userId: number;
    pageSize: number;
}

export type FetchOrderDetailAction = DeferredAction<number, Order | undefined>;
export type FetchPreviousOrdersAction = DeferredAction<OrdersPagePayload, Order[]>;
export type LoadMoreOrdersAction = DeferredAction<OrdersPagePayload, Order[]>;
export type GetOrderDetailAction = DeferredAction<number, Order | undefined>;

/**
 * Parity note (plan follow-up F2): the Zustand slice starts with `isLoading: true`
 * (lib/state/Order.ts:42) and this one with `false`. Left as-is on purpose — no hybrid reader
 * depends on the initial `false`, and aligning it is a behaviour change Mathieu decides.
 */
const initialState: OrderState = {
    previousOrders: [],
    isLoading: false,
    hasMore: false,
    currentPage: 0,
    order: undefined,
    isReorder: false
};

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        setOrder: (state, action: PayloadAction<Order | undefined>) => {
            state.order = action.payload;
        },
        setIsReorder: (state, action: PayloadAction<boolean>) => {
            state.isReorder = action.payload;
        },
        resetIsReorder: (state) => {
            state.isReorder = false;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setPreviousOrders: (state, action: PayloadAction<Order[]>) => {
            state.previousOrders = action.payload;
        },
        appendPreviousOrders: (state, action: PayloadAction<Order[]>) => {
            state.previousOrders = state.previousOrders.concat(action.payload);
        },
        setHasMore: (state, action: PayloadAction<boolean>) => {
            state.hasMore = action.payload;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        resetOrder: (state) => {
            return initialState;
        },
        // Saga triggers (deferred in `meta`, see the action types above)
        fetchOrderDetailRequest: {
            reducer: (state, _action: FetchOrderDetailAction) => {
                state.isLoading = true;
            },
            prepare: withDeferred<number, Order | undefined>()
        },
        fetchPreviousOrdersRequest: {
            reducer: (state, _action: FetchPreviousOrdersAction) => {
                state.isLoading = true;
            },
            prepare: withDeferred<OrdersPagePayload, Order[]>()
        },
        loadMoreOrdersRequest: {
            reducer: (state, _action: LoadMoreOrdersAction) => {
                state.isLoading = true;
            },
            prepare: withDeferred<OrdersPagePayload, Order[]>()
        },
        /**
         * `useOrder().getOrderDetail(orderId)` (lib/hooks/useOrder.legacy.ts:13-40): previous
         * orders first, then the API, WITHOUT touching `order` / `isLoading` — the hook keeps
         * its own `isLoadingDetail`. Saga-only; always awaited through the deferred.
         */
        getOrderDetailRequest: sagaRequest<number, Order | undefined>()
    },
    extraReducers: (builder) => {
        // `clearLoggedInState` → `resetOrder()` (lib/hooks/useAuth.ts:118).
        builder.addCase(loggedOut, () => initialState);
    }
});

export const {
    setOrder,
    setIsReorder,
    resetIsReorder,
    setLoading,
    setPreviousOrders,
    appendPreviousOrders,
    setHasMore,
    setCurrentPage,
    resetOrder,
    fetchOrderDetailRequest,
    fetchPreviousOrdersRequest,
    loadMoreOrdersRequest,
    getOrderDetailRequest
} = orderSlice.actions;

export const selectOrderState = (state: { order: OrderState }) => state.order;
export const selectOrder = (state: { order: OrderState }) => state.order.order;
export const selectIsReorder = (state: { order: OrderState }) => state.order.isReorder;

export default orderSlice.reducer;
